import React, { useState, useEffect } from 'react';

import { Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { FormTextField, FormSelect, FormNumberFormatField } from 'components/forms/FormikFields';
import { Formik, Form } from 'formik';

import ServiceService from 'services/ServiceService';
import DurationUtils from 'commons/utils/durationUtils';
import yup from 'commons/validations';
import ColorPicker from 'components/pickers/ColorPicker';
import showNotification from 'commons/helpers/showNotification';
import { isAppTypeCourtRental } from 'commons/helpers/appTypeSettings';

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  content: {
    marginTop: theme.spacing(2),
  },
  actionsRoot: {
    justifyContent: 'space-between',
  },
}));

const schema = yup.object().shape({
  name: yup.string().min(2).max(50).required(),
  duration: yup.number().min(5).required(),
  price: yup.number().min(0).required(),
  priceCost: yup.number().min(0).nullable(),
  colorId: yup.number().nullable(),
});

const DEFAULT_VALUES = { name: '', duration: 30, price: '', priceCost: '', colorId: null };
const DEFAULT_VALUES_COURT = { name: '', duration: 60, price: '', priceCost: '', colorId: null };

// COMPONENT
function ServiceFormModal({ open, defaultName, onClose, onCreateCallback }) {
  const { classes } = useStyles();
  const isCourtRental = isAppTypeCourtRental();

  const [initialValues, setInitialValues] = useState(isCourtRental ? DEFAULT_VALUES_COURT : DEFAULT_VALUES);

  useEffect(() => {
    async function initData() {
      setInitialValues({
        ...(isCourtRental ? DEFAULT_VALUES_COURT : DEFAULT_VALUES),
        name: defaultName ?? '',
        timestamp: new Date().getTime(),
      });
    }
    if (open) {
      initData();
    }
  }, [open, defaultName, isCourtRental]);

  const myHandleSubmit = (values, actions) => {
    if (
      values.priceCost != null &&
      values.priceCost !== '' &&
      values.priceCost !== '0.00' &&
      values.price === values.priceCost
    ) {
      actions.setSubmitting(false);
      showNotification(
        'warning',
        '"Preço do Serviço" não pode ser igual ao "Custo do Serviço".\nCusto refere-se a despesa de prestação daquele serviço.\nContate o suporte em caso de dúvidas.',
      );
      return;
    }

    const submission = {
      ...values,
    };
    ServiceService.save(submission)
      .then((created) => {
        actions.setSubmitting(false);
        onCreateCallback(created);
      })
      .catch(() => {
        actions.setSubmitting(false);
      });
  };

  return (
    <Dialog onClose={onClose} aria-labelledby="customized-dialog-title" open={open} fullWidth maxWidth="xs">
      <DialogTitle>
        <Typography variant="h5" component="span">
          Criando Serviço
        </Typography>
      </DialogTitle>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={myHandleSubmit}
      >
        {({ dirty, isSubmitting, values, setFieldValue }) => (
          <Form>
            <DialogContent dividers>
              <Box display="flex" flexDirection="row" alignItems="center">
                <FormTextField fullWidth label="Nome" name="name" />
                <ColorPicker
                  showRomoveSelection
                  removeSelectionText="Manter minha cor da agenda"
                  label="Selecione a cor de identificação"
                  selectedColorId={values.colorId}
                  setSelectedColorId={(colorId) => {
                    setFieldValue('colorId', colorId);
                  }}
                />
              </Box>
              <Box display="flex" flexDirection="row" alignItems="center">
                <FormSelect
                  fullWidth
                  label="Duração"
                  name="duration"
                  dataSource={DurationUtils.getDurationList()}
                />
              </Box>
              <Box display="flex" flexDirection="row" alignItems="center">
                <FormNumberFormatField fullWidth label="Preço" name="price" />
              </Box>
              <Box display="flex" flexDirection="row" alignItems="center">
                <FormNumberFormatField
                  fullWidth
                  label="Custo do Serviço (Utilizada para lançamento de despesas)"
                  name="priceCost"
                />
              </Box>
            </DialogContent>

            <DialogActions
              classes={{
                root: classes.actionsRoot,
              }}
            >
              <Button variant="outlined" onClick={() => onClose()}>
                Fechar
              </Button>
              <Button
                variant="contained"
                type="submit"
                color="primary"
                disabled={(!dirty && !defaultName) || isSubmitting}
              >
                Salvar
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

export default ServiceFormModal;
